import React from "react";

const SkeletonLine = ({ width = "100%", height = "h-4", className = "" }) => (
  <div className={`${height} rounded bg-black/[0.06] dark:bg-white/[0.06] animate-skeleton ${className}`} style={{ width }} />
);

const SkeletonCircle = ({ size = "w-10 h-10" }) => (
  <div className={`${size} rounded-full bg-black/[0.06] dark:bg-white/[0.06] animate-skeleton`} />
);

export const DashboardSkeleton = ({ role = "admin" }) => {
  return (
    <div className="min-h-screen bg-background flex" data-testid="dashboard-skeleton">
      {/* Sidebar skeleton (admin only) */}
      {role === "admin" && (
        <aside className="hidden lg:flex flex-col w-64 min-h-screen bg-[#111114] border-r border-white/[0.06] fixed">
          <div className="p-6">
            <SkeletonLine width="120px" height="h-8" />
          </div>
          <nav className="flex-1 px-4 space-y-2 mt-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <div className="w-5 h-5 rounded bg-white/[0.06] animate-skeleton" />
                <SkeletonLine width={`${60 + Math.random() * 40}%`} height="h-4" />
              </div>
            ))}
          </nav>
        </aside>
      )}

      {/* Main content skeleton */}
      <main className={`flex-1 ${role === "admin" ? "lg:ml-64" : ""}`}>
        {/* Top bar for staff/driver */}
        {role !== "admin" && (
          <div className="h-16 border-b border-black/[0.06] flex items-center justify-between px-6">
            <SkeletonLine width="120px" height="h-8" />
            <div className="flex items-center gap-3">
              <SkeletonCircle size="w-8 h-8" />
              <SkeletonLine width="80px" height="h-4" />
            </div>
          </div>
        )}

        <div className="p-6 lg:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="space-y-2">
              <SkeletonLine width="200px" height="h-7" />
              <SkeletonLine width="160px" height="h-4" />
            </div>
            <SkeletonLine width="140px" height="h-10" className="rounded-full" />
          </div>

          {/* Stats cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="p-4 rounded-xl border border-black/[0.06] bg-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-black/[0.04] animate-skeleton" />
                  <div className="space-y-1.5">
                    <SkeletonLine width="48px" height="h-6" />
                    <SkeletonLine width="64px" height="h-3" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Table skeleton */}
          <div className="rounded-xl border border-black/[0.06] bg-card overflow-hidden">
            <div className="p-4 border-b border-black/[0.06] flex gap-3">
              <SkeletonLine width="200px" height="h-9" className="rounded-lg" />
              <SkeletonLine width="120px" height="h-9" className="rounded-lg" />
            </div>
            <div className="divide-y divide-black/[0.04]">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-4 py-3.5">
                  <SkeletonLine width="100px" height="h-4" />
                  <SkeletonLine width="120px" height="h-4" />
                  <SkeletonLine width="180px" height="h-4" className="hidden md:block" />
                  <SkeletonLine width="90px" height="h-4" className="hidden lg:block" />
                  <SkeletonLine width="70px" height="h-6" className="rounded-full" />
                  <div className="flex-1" />
                  <SkeletonLine width="80px" height="h-8" className="rounded-lg" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardSkeleton;
